import type { DbDataChangedNotificationDtoType } from '@/objects/generated/';
import {
    SignalrContestantImportStatusSubscribeParameters,
    ContestantImportRowStatus,
    SignalrContestantsUpdatedSubscribeParameters,
    ContestantBase,
    SignalrContestantTimesSubscribeParameters,
    ContestantTime,
    SignalrDbDataChangedSubscribeParameters,
    DbDataChangedNotification,
    SignalrDeviceStatusSubscribeParameters,
    DeviceStatus,
    SignalrEventProductionDeviceSubscribeParameters,
    EventProductionDevice,
    SignalrFileArchiveItemSubscribeParameters,
    FileArchiveItem,
    SignalrGPIOReadingSubscribeParameters,
    GPIOReading,
    SignalrHeatPlanBackupItemSubscribeParameters,
    HeatPlanBackupItem,
    SignalrProductionDeviceSettingSubscribeParameters,
    ProductionDeviceSetting,
    SignalrProductionDisplayDataItemSubscribeParameters,
    ProductionDisplayDataItem,
    SignalrProductionKeepAliveSubscribeParameters,
    ProductionKeepAlive,
    SignalrRaceContestantTimeSubscribeParameters,
    RaceContestantTime,
    SignalrRankedTimeSubscribeParameters,
    RankedTime,
    SignalrResultItemSubscribeParameters,
    ResultItem,
    SignalrShootingResultSubscribeParameters,
    ShootingResult,
    SignalrStatusLoaderStatusParameters,
    StatusLoaderStatus,
    SignalrTrackingDeviceStatusSubscribeParameters,
    TrackingDeviceStateStatus,
    SignalrUnknownTagSubscribeParameters,
    UnknownTag,
} from '@/objects/generated/';
import { subscribeSignalr } from '@/signalr/signalrSubscriptionHandler';

const signalrSubscribe = {
    ContestantImportRowStatus: (args: { ImportGuid?: string | null }, callback: (data: ContestantImportRowStatus[]) => void) => {
        return subscribeSignalr(
            ContestantImportRowStatus,
            'ContestantImportRowStatus',
            SignalrContestantImportStatusSubscribeParameters.FromJsValues({ ImportGuid: args.ImportGuid ?? null }),
            callback,
        );
    },

    ContestantBase: (args: { EventID: number }, callback: (data: ContestantBase[]) => void) => {
        return subscribeSignalr(
            ContestantBase,
            'ContestantBase',
            SignalrContestantsUpdatedSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    ContestantTime: (args: { EventID: number; ContestantID?: number | null }, callback: (data: ContestantTime[]) => void) => {
        return subscribeSignalr(
            ContestantTime,
            'ContestantTime',
            SignalrContestantTimesSubscribeParameters.FromJsValues({
                EventID: args.EventID ?? null,
                ContestantID: args.ContestantID ?? null,
            }),
            callback,
        );
    },

    DbDataChangedNotification: (
        args: { EventID?: number | null; OrganizerID?: number | null; DtoType: DbDataChangedNotificationDtoType },
        callback: (data: DbDataChangedNotification[]) => void,
    ) => {
        return subscribeSignalr(
            DbDataChangedNotification,
            'DbDataChangedNotification',
            SignalrDbDataChangedSubscribeParameters.FromJsValues({
                EventID: args.EventID ?? null,
                OrganizerID: args.OrganizerID ?? null,
                DtoType: args.DtoType ?? null,
            }),
            callback,
        );
    },

    DeviceStatus: (callback: (data: DeviceStatus[]) => void) => {
        return subscribeSignalr(DeviceStatus, 'DeviceStatus', SignalrDeviceStatusSubscribeParameters.FromJsValues({}), callback);
    },

    EventProductionDevice: (args: { EventID: number }, callback: (data: EventProductionDevice[]) => void) => {
        return subscribeSignalr(
            EventProductionDevice,
            'EventProductionDevice',
            SignalrEventProductionDeviceSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    FileArchiveItem: (args: { EventID: number }, callback: (data: FileArchiveItem[]) => void) => {
        return subscribeSignalr(
            FileArchiveItem,
            'FileArchiveItem',
            SignalrFileArchiveItemSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    GPIOReading: (args: { EventID: number }, callback: (data: GPIOReading[]) => void) => {
        return subscribeSignalr(
            GPIOReading,
            'GPIOReading',
            SignalrGPIOReadingSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    HeatPlanBackupItem: (args: { HeatPlanID: number }, callback: (data: HeatPlanBackupItem[]) => void) => {
        return subscribeSignalr(
            HeatPlanBackupItem,
            'HeatPlanBackupItem',
            SignalrHeatPlanBackupItemSubscribeParameters.FromJsValues({ HeatPlanID: args.HeatPlanID ?? null }),
            callback,
        );
    },

    ProductionDeviceSetting: (args: { DeviceID?: string | null }, callback: (data: ProductionDeviceSetting[]) => void) => {
        return subscribeSignalr(
            ProductionDeviceSetting,
            'ProductionDeviceSetting',
            SignalrProductionDeviceSettingSubscribeParameters.FromJsValues({ DeviceID: args.DeviceID ?? null }),
            callback,
        );
    },

    ProductionDisplayDataItem: (args: { EventID: number }, callback: (data: ProductionDisplayDataItem[]) => void) => {
        return subscribeSignalr(
            ProductionDisplayDataItem,
            'ProductionDisplayDataItem',
            SignalrProductionDisplayDataItemSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    ProductionKeepAlive: (args: { EventID: number }, callback: (data: ProductionKeepAlive[]) => void) => {
        return subscribeSignalr(
            ProductionKeepAlive,
            'ProductionKeepAlive',
            SignalrProductionKeepAliveSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    RaceContestantTime: (args: { EventID: number }, callback: (data: RaceContestantTime[]) => void) => {
        return subscribeSignalr(
            RaceContestantTime,
            'RaceContestantTime',
            SignalrRaceContestantTimeSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    RankedTime: (args: { EventID: number; RaceContestantID?: number | null }, callback: (data: RankedTime[]) => void) => {
        return subscribeSignalr(
            RankedTime,
            'RankedTime',
            SignalrRankedTimeSubscribeParameters.FromJsValues({
                EventID: args.EventID ?? null,
                RaceContestantID: args.RaceContestantID ?? null,
            }),
            callback,
        );
    },

    ResultItem: (args: { EventID: number }, callback: (data: ResultItem[]) => void) => {
        return subscribeSignalr(
            ResultItem,
            'ResultItem',
            SignalrResultItemSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    ShootingResult: (args: { EventID: number }, callback: (data: ShootingResult[]) => void) => {
        return subscribeSignalr(
            ShootingResult,
            'ShootingResult',
            SignalrShootingResultSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },

    StatusLoaderStatus: (args: { UserID: number }, callback: (data: StatusLoaderStatus[]) => void) => {
        return subscribeSignalr(
            StatusLoaderStatus,
            'StatusLoaderStatus',
            SignalrStatusLoaderStatusParameters.FromJsValues({ UserID: args.UserID ?? null }),
            callback,
        );
    },

    TrackingDeviceStateStatus: (callback: (data: TrackingDeviceStateStatus[]) => void) => {
        return subscribeSignalr(
            TrackingDeviceStateStatus,
            'TrackingDeviceStateStatus',
            SignalrTrackingDeviceStatusSubscribeParameters.FromJsValues({}),
            callback,
        );
    },

    UnknownTag: (args: { EventID: number }, callback: (data: UnknownTag[]) => void) => {
        return subscribeSignalr(
            UnknownTag,
            'UnknownTag',
            SignalrUnknownTagSubscribeParameters.FromJsValues({ EventID: args.EventID ?? null }),
            callback,
        );
    },
};

export default signalrSubscribe;
